import { Fragment, useState } from 'react';
import type { CSSProperties, PointerEventHandler, ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { DragHandle, SortableList } from '../dnd/Sortable';
import { Icon } from './Icon';
import { IncludeExcludeControl, type IncludeRecommendation } from './IncludeExcludeControl';
import { ProgressBar, type ProgressBarTone } from './ProgressBar';
import { RoleChooser } from './RoleChooser';
import { StructureStateChip, type StructureNote } from './StructureStateChip';

export type IncludeState = 'none' | 'included' | 'excluded';

export type TestCaseRow = {
  id: string;
  testId: string;
  /**
   * F-023 positional Displayed Test ID — the case's 1-based position in its
   * subsection's curated order (e.g. `DO-01`). Rendered as the PRIMARY Test ID
   * with `testId` kept as the secondary handle cue. Absent ⇒ the cell falls back
   * to `testId`. Display-only and NOT unique across subsections, so every
   * aria-label / `data-*` hook keys on `testId`, never this (R4/FR-004).
   */
  displayTestId?: string;
  function: string;
  roles?: string[];
  testCase: string;
  /**
   * Template Match percentage 0–100: the share of the case's Content Items
   * verbatim from the template (derived from item Origins — NOT the engine's
   * selection score). Ignored when `novel` is true.
   */
  templateMatch: number;
  /** Origin breakdown behind the % (e.g. "10 of 12 items verbatim from template · 2 novel"). */
  templateMatchLabel?: string;
  /** Reviewer-added case — renders "Novel" in place of a match percentage. */
  novel?: boolean;
  /** The designer's Inclusion Decision → the control's FILL. */
  includeState?: IncludeState;
  /** The AI's recommendation → the control's OUTLINE. */
  recommendation?: IncludeRecommendation;
  completed?: boolean;
  selected?: boolean;
  /**
   * F-017 (US2): the honest structure-state note for a non-default case (e.g.
   * excluded by a reviewer or a variant rule). Absent for the template-home
   * happy path. Rendered as an inline chip beside the case name — never hidden.
   */
  structureNote?: StructureNote;
  /**
   * The case's Content Items (steps). When present, the Test-case cell's caret
   * toggles a read-only inline dropdown of these items (Figma 1518-80731)
   * instead of navigating into the detail card.
   */
  contentItems?: ReadonlyArray<{ id: string; body: string; kind: string; origin?: string }>;
};

export type TestCaseTableProps = {
  /** Optional "GENERAL INSTRUCTIONS" block shown above the table. */
  instructions?: ReactNode;
  rows: TestCaseRow[];
  /**
   * Action-column rendering. Defaults to `actions` (Exclude/Include
   * +/− pair). `status` shows a finalised pill instead — used in the
   * uat-27 included-rows view.
   */
  actionMode?: 'actions' | 'status';
  onSelectRow?: (id: string) => void;
  onIncludeChange?: (id: string, next: IncludeState) => void;
  /** Clear the row's Inclusion Decision back to undecided (deselect). */
  onClearInclude?: (id: string) => void;
  /** Hide the Completed column for section variants where no row can be marked complete. */
  showCompleted?: boolean;
  onCompletedChange?: (id: string, next: boolean) => void;
  /**
   * F-010 (T037): the trial's Allowed-Role Set, constraining the live role
   * chooser. Empty ⇒ no matrix ⇒ the chooser disables adding with a prompt.
   */
  allowedRoles?: string[];
  /**
   * F-010 (T037): full-list role replace for a row. When provided, the role
   * cell becomes the live chooser; absent ⇒ roles render read-only.
   */
  onRolesChange?: (id: string, next: string[]) => void;
  /**
   * Feature 015: render each row as a sortable with a grip-handle column and
   * make the table frame an append drop target for cross-subsection drags.
   * Requires an `AssemblyDnd` ancestor.
   */
  sortable?: { readonly subsectionId: string };
  /**
   * When provided, a hover-revealed "New Test Case" insertion line (Figma
   * 1504-74035) renders above the first row and after each row; clicking it
   * adds a case. Absent ⇒ no inline insertion affordance.
   */
  onInsertCase?: () => void;
  /** Disables the inline insertion lines while a create request is in flight. */
  inserting?: boolean;
};

function matchTone(pct: number): ProgressBarTone {
  if (pct >= 90) return 'success';
  if (pct >= 70) return 'brand';
  if (pct >= 40) return 'warning';
  return 'danger';
}

const FRAME_CLASS = 'px-3 pt-[10px] pb-3';

// Excluded rows are struck out with a faint diagonal hatch (Figma 584-16085),
// painted on the <tr> so it reads seamlessly across the transparent cells.
const EXCLUDED_HATCH =
  'repeating-linear-gradient(120deg, rgba(217,217,217,0.3) 0, rgba(217,217,217,0.3) 3px, transparent 3px, transparent 11px)';

/** The table frame as an append drop target (drop into blank space ⇒ end). */
function DroppableFrame({ subsectionId, children }: { subsectionId: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `caseListDrop:${subsectionId}`,
    data: { kind: 'caseListDrop', subsectionId },
  });
  return (
    <div
      ref={setNodeRef}
      data-test-case-table-frame
      data-drop-over={isOver ? 'true' : 'false'}
      className={clsx(FRAME_CLASS, isOver && 'bg-ink-50')}
    >
      {children}
    </div>
  );
}

/**
 * A sortable `<tr>` (feature 015): the grip handle starts drags; the row's
 * own click/controls keep working (the pointer sensor's activation distance
 * separates a click from a drag). Hooks need a component boundary, hence the
 * split from the plain-row path.
 */
function SortableCaseRow({
  id,
  label,
  selected,
  onClick,
  className,
  hatch = false,
  children,
}: {
  id: string;
  label: string;
  selected: boolean;
  onClick: () => void;
  className: string;
  hatch?: boolean;
  children: ReactNode;
}) {
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id,
    data: { kind: 'testCase', label },
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform) ?? '',
    ...(transition ? { transition } : {}),
    ...(hatch ? { backgroundImage: EXCLUDED_HATCH } : {}),
  };
  // Pointer-only activator on the row: dropping the keyboard listener avoids the
  // sortable's Space/Enter activation eating those keys for inputs inside the row
  // (e.g. the role chooser). Keyboard reordering stays on the sr-only handle.
  // dnd-kit types `listeners` loosely (a Record<string, Function> map); narrow the
  // single activator we forward to the row to its precise React handler type so the
  // <tr> spread typechecks. Runtime-identical to forwarding the raw listener.
  const onPointerDown = listeners?.['onPointerDown'] as
    | PointerEventHandler<HTMLTableRowElement>
    | undefined;
  const pointerListeners = onPointerDown ? { onPointerDown } : {};
  return (
    // The whole row is the pointer-drag target (no grip column); the pointer
    // sensor's activation distance separates a click from a drag, so the row's
    // own controls keep working. A visually-hidden handle in the first cell
    // preserves keyboard reordering + the a11y/test locator.
    <tr
      ref={setNodeRef}
      data-selected={selected ? 'true' : 'false'}
      data-dragging={isDragging ? 'true' : 'false'}
      onClick={onClick}
      style={style}
      className={clsx('touch-none', className, isDragging && 'opacity-50')}
      {...pointerListeners}
    >
      <td className="w-0 p-0">
        <DragHandle label={label} handleProps={{ ...attributes, ...listeners }} srOnly />
      </td>
      {children}
    </tr>
  );
}

/**
 * Inner table shown when a UAT subsection is expanded (uat-02 reference).
 * Columns: Test ID / Function / Roles / Test case / Template Match /
 * Exclude+Include, with Completed shown only for completable sections.
 */
export function TestCaseTable({
  instructions,
  rows,
  actionMode = 'actions',
  showCompleted = true,
  onSelectRow,
  onIncludeChange,
  onClearInclude,
  onCompletedChange,
  allowedRoles,
  onRolesChange,
  sortable,
  onInsertCase,
  inserting = false,
}: TestCaseTableProps) {
  const actionColumnLabel = actionMode === 'status' ? 'Status' : 'Exclude/Include';
  const columnCount = (sortable ? 1 : 0) + 6 + (showCompleted ? 1 : 0);
  // Inline content dropdown (Figma 1518-80731): the Test-case caret toggles a
  // read-only preview of the row's Content Items rather than navigating.
  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // The hover-revealed "+ New Test Case" line shown above the first row and
  // after each row (Figma 1504-74035). Sits in a zero-height spacer so it never
  // shifts the row rhythm; the pill overflows onto the gap on hover.
  const insertBar = (key: string, ariaLabel: string): ReactNode =>
    onInsertCase ? (
      <tr key={key} data-insert-gap="true">
        <td colSpan={columnCount} className="relative h-0 p-0">
          <button
            type="button"
            aria-label={ariaLabel}
            disabled={inserting}
            onClick={(e) => {
              e.stopPropagation();
              onInsertCase();
            }}
            className="group/insert absolute inset-x-0 top-0 z-[3] flex h-[14px] -translate-y-1/2 items-center justify-center disabled:cursor-not-allowed"
          >
            <span
              aria-hidden="true"
              className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-[#515ffc] opacity-0 transition-opacity group-hover/insert:opacity-100 group-focus-within/insert:opacity-100"
            />
            <span className="relative z-[1] inline-flex h-[18px] items-center gap-1 rounded-pill bg-[#4c70f0] px-2 text-center font-['IBMPlexSansDevanagari'] text-[12px] font-normal leading-[1.4] tracking-[-0.15px] text-white opacity-0 transition-opacity group-hover/insert:opacity-100 group-focus-within/insert:opacity-100">
              <Icon name="plus" size={12} /> New Test Case
            </span>
          </button>
        </td>
      </tr>
    ) : null;
  const wrapRows = (nodes: ReactNode): ReactNode =>
    sortable ? <SortableList ids={rows.map((r) => r.id)}>{nodes}</SortableList> : nodes;

  const table = (
    // No overflow-clip: the hover-insertion line/pill must be able to overflow
    // the table edges instead of being cut off (the rounded corners still read
    // because the cells inside are inset by their borders).
    <div className="rounded-md bg-surface-card">
      <table className="w-full table-fixed border-collapse text-sm leading-[17px]">
        <colgroup data-test-case-table-columns>
          {sortable && <col className="w-0" />}
          <col className="w-[92px]" />
          <col className="w-[170px]" />
          <col className="w-[148px]" />
          <col className="w-auto" />
          <col className="w-[128px]" />
          <col className="w-[127px]" />
          {showCompleted && <col className="w-[92px]" />}
        </colgroup>
        <thead>
          <tr className="h-[33px]">
            {sortable && <th aria-hidden="true" className="w-0 p-0" />}
            {[
              { l: 'Test ID' },
              { l: 'Function' },
              { l: 'Roles' },
              { l: 'Test case' },
              { l: 'Template Match', w: 'w-[100px]' },
              { l: actionColumnLabel, w: 'w-[127px]' },
              ...(showCompleted ? [{ l: 'Completed', w: 'w-[90px]' }] : []),
            ].map((h, i, arr) => (
              <th
                key={h.l}
                className={clsx(
                  // Figma 1518-80731 header cell: centred, 8px vertical padding,
                  // 12px regular #292938, with 1px #E7E8EA dividers between columns.
                  'whitespace-nowrap border-b border-border px-0 py-2 text-center align-middle',
                  'text-[12px] font-normal leading-[1.4] tracking-[-0.15px] text-[#292938]',
                  i < arr.length - 1 && 'border-r border-[#e7e8ea]',
                  h.w,
                )}
              >
                {h.l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {wrapRows(
            rows.map((r, idx) => {
              const includeState: IncludeState = r.includeState ?? 'none';
              const isIncluded = includeState === 'included';
              const isExcluded = includeState === 'excluded';
              const decided = isIncluded || isExcluded;
              // The decided accent (Figma 1061-113867 / 584-16085): green when
              // included, red when excluded — drives the Test ID fill and the
              // Test-case left border.
              const accent = isIncluded ? '#1d7c5f' : isExcluded ? '#d94433' : null;
              const isLast = idx === rows.length - 1;
              const contentItems = r.contentItems ?? [];
              const hasContentItems = contentItems.length > 0;
              const isRowExpanded = expandedRows.has(r.id);
              const roles = r.roles ?? [];
              const primaryRole = roles[0] ?? null;
              const extraRoleCount = Math.max(0, roles.length - 1);
              // F-010 (FR-019/AS4): completion is NEVER gated on having a role —
              // the role-required-to-complete gate was removed. The Completed
              // affordance shows wherever the column is shown.
              const canComplete = showCompleted;
              // Row navigation is opt-in: clickable only when a selection handler is
              // wired (disabled for now while the single test-case view is reworked).
              // F-038 US1 (FR-003): an undecided row cannot be marked complete from
              // the list — completion needs an explicit Inclusion Decision first. An
              // already-complete row is unaffected (its check stays shown).
              const completionBlocked = r.completed !== true && includeState === 'none';
              const rowClassName = clsx(
                'relative h-[36px]',
                onSelectRow ? 'cursor-pointer hover:bg-ink-50' : 'cursor-default',
                r.selected &&
                  'after:pointer-events-none after:absolute after:inset-0 after:z-0 after:rounded-md after:bg-ink-50 after:content-[""]',
              );
              const cells = (
                <>
                  <td
                    className={clsx(
                      'relative z-[1] border-b border-border px-3 py-0 text-center align-middle',
                      isLast && 'border-b-0',
                      // Decided rows get a solid colour-coded Test ID cell with
                      // white text (Figma 1061-113867 / 584-16085).
                      decided ? 'text-white' : 'text-text-primary',
                    )}
                    {...(accent ? { style: { backgroundColor: accent } } : {})}
                  >
                    {/* F-023: the positional Displayed Test ID is the primary
                        value; the original Master-Template handle stays visible
                        as a secondary cue (hybrid). When they coincide we show
                        one value (no DO-04/DO-04 redundancy). The number is
                        display-only — every hook below keys on r.testId. */}
                    <span className="flex flex-col items-center leading-tight">
                      {onSelectRow ? (
                        // The Test ID is the explicit link into the case's detail
                        // page — a real <button> so it is keyboard-reachable with an
                        // accessible name keyed on the stable testId (never the
                        // positional displayTestId, FR-004). stopPropagation keeps it
                        // from also bubbling to the row's own click (no double-open).
                        // Colour inherits the cell (white on a decided green/red fill,
                        // ink otherwise); hover/focus underline + ring read on both.
                        <button
                          type="button"
                          aria-label={`Open test case ${r.testId}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectRow(r.id);
                          }}
                          className={clsx(
                            'block max-w-full cursor-pointer truncate rounded-sm bg-transparent text-[14px] font-normal leading-[1.4] tracking-[-0.15px]',
                            'hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current',
                            decided ? 'text-white' : 'text-[#292938]',
                          )}
                        >
                          {r.displayTestId ?? r.testId}
                        </button>
                      ) : (
                        <span
                          className={clsx(
                            'block max-w-full truncate text-[14px] font-normal leading-[1.4] tracking-[-0.15px]',
                            decided ? 'text-white' : 'text-[#292938]',
                          )}
                        >
                          {r.displayTestId ?? r.testId}
                        </span>
                      )}
                      {/* Secondary Master-Template handle: visually hidden for now
                          (kept in the DOM as the stable locator / a11y cue). */}
                      {r.displayTestId !== undefined && r.displayTestId !== r.testId && (
                        <span data-test-id-handle={r.testId} className="sr-only">
                          {r.testId}
                        </span>
                      )}
                    </span>
                  </td>
                  <td
                    className={clsx(
                      'relative z-[1] border-b border-l border-border border-l-[#e7e8ea] px-3 py-0 align-middle',
                      isLast && 'border-b-0',
                    )}
                  >
                    <span className="block max-w-[140px] truncate text-[14px] font-normal leading-[1.4] tracking-[-0.28px] text-[#111]">
                      {r.function}
                    </span>
                  </td>
                  <td
                    className={clsx(
                      'relative z-[1] border-b border-l border-border border-l-[#e7e8ea] px-3 py-0 text-center align-middle',
                      isLast && 'border-b-0',
                    )}
                  >
                    {onRolesChange ? (
                      // F-010 (T037): the live, Allowed-Role-Set-constrained chooser —
                      // identical behaviour to the detail view (FR-014).
                      <div
                        data-role-cell={r.testId}
                        className="flex w-full min-w-0 items-center justify-center"
                      >
                        <RoleChooser
                          roles={roles}
                          allowedRoles={allowedRoles ?? []}
                          onChange={(next) => onRolesChange(r.id, next)}
                          variant="compact"
                          label={r.testId}
                        />
                      </div>
                    ) : (
                      // Read-only summary (finalised / non-editable views).
                      <div
                        data-role-cell={r.testId}
                        className="flex w-full min-w-0 items-center justify-center gap-1"
                      >
                        {primaryRole && (
                          <>
                            <span
                              title={primaryRole}
                              className="inline-flex h-6 min-w-0 flex-1 items-center rounded-sm bg-purple-100 px-1 py-1 text-xs font-semibold leading-none text-purple-800"
                            >
                              <span className="truncate">{primaryRole}</span>
                            </span>
                            {extraRoleCount > 0 && (
                              <span
                                aria-label={`${extraRoleCount} more ${extraRoleCount === 1 ? 'role' : 'roles'} for ${r.testId}`}
                                className="inline-flex h-6 min-w-6 items-center justify-center rounded-sm bg-ink-100 px-1 text-xs font-semibold text-text-secondary"
                              >
                                +{extraRoleCount}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td
                    className={clsx(
                      'relative z-[1] border-b border-l border-border px-3 py-0 align-middle text-text-primary',
                      isLast && 'border-b-0',
                      // Decided rows carry the accent down the Test-case cell's
                      // left edge (Figma 1061-113867 / 584-16085); otherwise a
                      // 1px column divider.
                      decided ? 'border-l-[3px]' : 'border-l-[#e7e8ea]',
                    )}
                    {...(accent ? { style: { borderLeftColor: accent } } : {})}
                  >
                    {(() => {
                      const caret = (
                        // Figma 1518-80731 caret (stroke #000212), rotates when open.
                        <span className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center text-[#000212]">
                          <svg
                            data-icon="chevronRight"
                            width="6"
                            height="8"
                            viewBox="0 0 6 8"
                            fill="none"
                            aria-hidden="true"
                            className={clsx('transition-transform', isRowExpanded && 'rotate-90')}
                          >
                            <path
                              d="M0.329102 0.376282L4.3291 3.87628L0.329102 7.37628"
                              stroke="currentColor"
                            />
                          </svg>
                        </span>
                      );
                      const name = (
                        <span
                          title={r.testCase}
                          className={clsx(
                            'block min-w-0 truncate text-[14px] font-normal leading-[1.4] tracking-[-0.15px] text-[#292938]',
                            // Excluded cases are struck through (Figma 584-16085).
                            isExcluded && 'line-through opacity-60',
                          )}
                        >
                          {r.testCase}
                        </span>
                      );
                      const note = r.structureNote ? (
                        <span className="flex-shrink-0">
                          <StructureStateChip note={r.structureNote} />
                        </span>
                      ) : null;
                      // The whole Test-case field is the dropdown toggle (Figma
                      // 1518-80731): hover/click anywhere flips the caret + panel.
                      return hasContentItems ? (
                        <button
                          type="button"
                          aria-label={
                            isRowExpanded
                              ? `Hide steps for ${r.testId}`
                              : `Show steps for ${r.testId}`
                          }
                          aria-expanded={isRowExpanded}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(r.id);
                          }}
                          className="group/dropdown -mx-1 inline-flex w-[calc(100%+0.5rem)] min-w-0 cursor-pointer items-center gap-2 rounded-sm bg-transparent px-1 py-0.5 text-left transition-colors hover:bg-ink-50"
                        >
                          {caret}
                          {name}
                          {note}
                        </button>
                      ) : (
                        <span className="inline-flex w-full min-w-0 items-center gap-2">
                          {caret}
                          {name}
                          {note}
                        </span>
                      );
                    })()}
                  </td>
                  <td
                    className={clsx(
                      'relative z-[1] w-[100px] border-b border-l border-border border-l-[#e7e8ea] px-3 py-0 text-center align-middle',
                      isLast && 'border-b-0',
                    )}
                  >
                    {r.novel ? (
                      <span className="text-sm font-semibold italic text-purple-700">Novel</span>
                    ) : (
                      <div
                        className="flex flex-col items-center gap-[2px]"
                        {...(r.templateMatchLabel ? { title: r.templateMatchLabel } : {})}
                      >
                        <span className="text-center text-[12px] font-medium uppercase leading-[15px] tracking-[0.117px] tabular-nums text-black">
                          {Math.round(r.templateMatch)}%
                        </span>
                        <ProgressBar
                          value={r.templateMatch}
                          tone={matchTone(r.templateMatch)}
                          size="sm"
                          aria-label={`Template match ${Math.round(r.templateMatch)}%`}
                        />
                      </div>
                    )}
                  </td>
                  <td
                    className={clsx(
                      'relative z-[1] h-9 w-[127px] border-b border-l border-border border-l-[#e7e8ea] px-3 py-0 text-center align-middle',
                      isLast && 'border-b-0',
                      // Decided rows fill the cell with the INCLUDED/EXCLUDED field
                      // (Figma 1061-113867 / 587-15733).
                      actionMode === 'actions' && isIncluded && 'bg-[#dfede9] px-0',
                      actionMode === 'actions' && isExcluded && 'bg-[#fbebe9] px-0',
                    )}
                  >
                    {actionMode === 'status' && includeState !== 'none' ? (
                      <span
                        data-tone={includeState}
                        className={clsx(
                          'inline-flex min-w-[88px] items-center justify-center rounded-sm px-2 py-1 text-xs font-bold uppercase tracking-[0.06em]',
                          includeState === 'included' && 'bg-success-bg text-success-fg',
                          includeState === 'excluded' && 'bg-danger-bg text-danger-fg',
                        )}
                      >
                        {includeState === 'included' ? 'INCLUDED' : 'EXCLUDED'}
                      </span>
                    ) : decided ? (
                      // Decided: a full-cell interactive field; clicking reverses
                      // the row to needs-review (Figma 1061-113867 / 587-15733).
                      <button
                        type="button"
                        aria-label={`${isIncluded ? 'Included' : 'Excluded'} ${r.testId}, click to reverse`}
                        aria-pressed
                        onClick={(e) => {
                          e.stopPropagation();
                          onClearInclude?.(r.id);
                        }}
                        className={clsx(
                          'flex h-full w-full cursor-pointer items-center justify-center text-xs font-medium uppercase tracking-[-0.1504px]',
                          isIncluded ? 'text-[#1d7c5f]' : 'text-[#d94433]',
                        )}
                      >
                        {isIncluded ? 'Included' : 'Excluded'}
                      </button>
                    ) : (
                      <IncludeExcludeControl
                        size="compact"
                        compactSize="sm"
                        recommendation={r.recommendation ?? null}
                        selection={includeState === 'none' ? 'undecided' : includeState}
                        ariaSuffix={r.testId}
                        onInclude={() => onIncludeChange?.(r.id, 'included')}
                        onExclude={() => onIncludeChange?.(r.id, 'excluded')}
                        onClear={() => onClearInclude?.(r.id)}
                      />
                    )}
                  </td>
                  {showCompleted && (
                    <td
                      data-completed-cell={r.testId}
                      className={clsx(
                        'relative z-[1] w-[90px] border-b border-l border-border border-l-[#e7e8ea] px-3 py-0 text-center align-middle',
                        isLast && 'border-b-0',
                      )}
                    >
                      {canComplete && (
                        <button
                          type="button"
                          aria-label={
                            r.completed ? `${r.testId} completed` : `Mark ${r.testId} complete`
                          }
                          aria-pressed={r.completed ?? false}
                          data-completed={r.completed ? 'true' : 'false'}
                          disabled={completionBlocked}
                          {...(completionBlocked
                            ? {
                                title:
                                  'Decide Include or Exclude before marking this test case complete.',
                              }
                            : {})}
                          onClick={(e) => {
                            e.stopPropagation();
                            onCompletedChange?.(r.id, !r.completed);
                          }}
                          className={clsx(
                            // Fill the whole cell so the entire field is the
                            // hover/click target, with the seal centred. F-038:
                            // an undecided row's completion is blocked (dimmed).
                            'group/seal absolute inset-0 flex items-center justify-center',
                            completionBlocked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
                            r.completed ? 'text-[#1d7c5f]' : 'text-[#6d7287]',
                          )}
                        >
                          {/* Figma "seal" completion mark (node 553-8686): filled
                              green with a white check when complete, grey outline
                              previewing a green check on hover otherwise. */}
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill={r.completed ? 'currentColor' : 'none'}
                            aria-hidden="true"
                          >
                            <path
                              d="M11.0752 2.69336L11.2842 2.875L11.5488 2.79395L14.4141 1.91992L15.0908 4.83887L15.1533 5.1084L15.415 5.19824L18.2471 6.17578L17.0781 8.93457L16.9707 9.18945L17.1289 9.41602L18.8457 11.8711L16.2783 13.417L16.042 13.5596L16.0361 13.8359L15.9814 16.832L12.9971 16.5723L12.7217 16.5488L12.5547 16.7695L10.75 19.1611L8.4873 17.1973L8.27832 17.0156L8.01367 17.0967L5.14746 17.9707L4.47168 15.0518L4.40918 14.7822L4.14844 14.6914L1.31543 13.7139L2.48438 10.9561L2.59277 10.7012L2.43359 10.4736L0.716797 8.01758L3.28418 6.47363L3.52148 6.33105L3.52637 6.05469L3.58008 3.05762L6.56543 3.31738L6.8418 3.3418L7.00781 3.12109L8.8125 0.728516L11.0752 2.69336Z"
                              stroke="currentColor"
                            />
                            <path
                              d="M6.7 10.1L9 12.3L13.3 7.7"
                              fill="none"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={clsx(
                                r.completed
                                  ? 'stroke-white'
                                  : 'stroke-[#1d7c5f] opacity-0 group-hover/seal:opacity-100',
                              )}
                            />
                          </svg>
                        </button>
                      )}
                    </td>
                  )}
                </>
              );
              const rowNode = sortable ? (
                <SortableCaseRow
                  id={r.id}
                  label={r.testId}
                  selected={r.selected ?? false}
                  onClick={() => onSelectRow?.(r.id)}
                  className={rowClassName}
                  hatch={isExcluded}
                >
                  {cells}
                </SortableCaseRow>
              ) : (
                <tr
                  data-selected={r.selected ? 'true' : 'false'}
                  onClick={() => onSelectRow?.(r.id)}
                  className={rowClassName}
                  {...(isExcluded ? { style: { backgroundImage: EXCLUDED_HATCH } } : {})}
                >
                  {cells}
                </tr>
              );
              // Read-only inline content dropdown (Figma 1518-80731): an indented
              // panel with a connector rail listing the case's Content Items —
              // blue "instruction" notes and white "task" rows with a T badge.
              const contentDropdownRow =
                isRowExpanded && hasContentItems ? (
                  <tr data-test-case-content={r.testId}>
                    <td colSpan={columnCount} className="bg-[#e7e8ea] p-0">
                      <div className="flex items-stretch gap-[14px] bg-[#e7e8ea] pl-4">
                        <div aria-hidden="true" className="w-px flex-shrink-0 bg-[#94949b]" />
                        <div className="flex min-w-0 flex-1 flex-col gap-1 py-[10px]">
                          {contentItems.map((item) => {
                            if (item.kind === 'instruction') {
                              return (
                                <div
                                  key={item.id}
                                  className="rounded-[4px] bg-[#4c70f0]/15 px-2 py-1 text-xs leading-[1.4] text-[#515ffc]"
                                >
                                  {item.body}
                                </div>
                              );
                            }
                            if (item.kind === 'heading') {
                              return (
                                <div
                                  key={item.id}
                                  className="px-1 py-1 text-xs font-bold leading-[1.4] text-[#292938]"
                                >
                                  {item.body}
                                </div>
                              );
                            }
                            // The leading badge letter encodes the item's origin:
                            // T = Template (grey), N = Novel (#515FFC), E = Edited (#EA8B17),
                            // G = Grounded (#0D9488, #123). Without the explicit grounded arm
                            // a grounded item falls through to the "T" default and is mislabeled.
                            const badge =
                              item.origin === 'novel'
                                ? { letter: 'N', cls: 'bg-[#515ffc]/15 text-[#515ffc]' }
                                : item.origin === 'edited'
                                  ? { letter: 'E', cls: 'bg-[#ea8b17]/15 text-[#ea8b17]' }
                                  : item.origin === 'grounded'
                                    ? { letter: 'G', cls: 'bg-[#0d9488]/15 text-[#0d9488]' }
                                    : { letter: 'T', cls: 'bg-[#94949b]/15 text-[#94949b]' };
                            return (
                              <div
                                key={item.id}
                                className="flex min-h-9 items-center gap-2 rounded-[4px] bg-white p-2"
                              >
                                <span
                                  className={clsx(
                                    'inline-flex h-5 w-4 flex-shrink-0 items-center justify-center rounded-[3px] text-[11px] font-semibold',
                                    badge.cls,
                                  )}
                                >
                                  {badge.letter}
                                </span>
                                <span className="min-w-0 flex-1 text-[12px] font-normal leading-[1.4] tracking-[-0.15px] text-[#292938]">
                                  {item.body}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null;
              return (
                <Fragment key={r.id}>
                  {idx === 0 && insertBar(`insert-top`, 'Add test case at the top')}
                  {rowNode}
                  {contentDropdownRow}
                  {insertBar(`insert-after-${r.id}`, `Add test case after ${r.testId}`)}
                </Fragment>
              );
            }),
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-[#e0e3eb]">
      {/* General Instructions block (Figma 1061-101043): the caller supplies a
          self-contained node (the editable `GeneralInstructions`), so it is
          rendered as-is — label, body, and padding all come from the node. */}
      {instructions}
      {sortable ? (
        <DroppableFrame subsectionId={sortable.subsectionId}>{table}</DroppableFrame>
      ) : (
        <div data-test-case-table-frame className={FRAME_CLASS}>
          {table}
        </div>
      )}
    </div>
  );
}
